import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import SportsHandballRoundedIcon from '@mui/icons-material/SportsHandballRounded';
import SportsRoundedIcon from '@mui/icons-material/SportsRounded';
import AppLayout from '../../components/ui/AppLayout';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';
import { CORES_TIMES, IconeLance, LanceDialog, MarcadoresLance, contarLances, labelLance, nomeCurto } from '../../components/Lances';
import CheckinEquipe from '../../components/CheckinEquipe';
import PotesDialog from '../../components/PotesDialog';
import api from '../../api';
import tokens from '../../theme/tokens';

const TEMPO_PARTIDA = 10 * 60; // segundos
const cores = CORES_TIMES;
const formatar = (seg) => `${String(Math.floor(seg / 60)).padStart(2, '0')}:${String(seg % 60).padStart(2, '0')}`;

// Tela do árbitro: cronômetro, gols e cartões com placar automático e encerramento da partida.
const ArbitroPage = () => {
  const [estado, setEstado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [jogadorSelecionado, setJogadorSelecionado] = useState(null);
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false);
  const [goleirosAbertos, setGoleirosAbertos] = useState(false);
  const [goleiros, setGoleiros] = useState(['', '']);
  const [toast, setToast] = useState(null);
  const [escalaEquipe, setEscalaEquipe] = useState(null);
  const base = useRef({ segundos: 0, rodando: false, em: Date.now() });
  const avisou = useRef(false);

  const aplicarPartida = useCallback((partida) => {
    setEstado((atual) => (atual ? { ...atual, partida } : atual));
    if (partida) {
      base.current = { segundos: partida.cronometro.segundos, rodando: partida.cronometro.status === 'rodando', em: Date.now() };
      setSegundos(partida.cronometro.segundos);
    }
  }, []);

  const carregar = useCallback(async (silencioso) => {
    if (!silencioso) setCarregando(true);
    try {
      const response = await api.get('arbitragem/estado');
      setEstado(response.data);
      // Árbitro com login próprio precisa estar escalado e com check-in feito
      const minha = response.data.minhaEscala;
      if (minha && (!minha.escalado || !minha.checkinEm)) {
        const escala = await api.get('equipe/minha-escala');
        setEscalaEquipe(escala.data);
      } else {
        setEscalaEquipe(null);
      }
      const partida = response.data.partida;
      if (partida) {
        base.current = { segundos: partida.cronometro.segundos, rodando: partida.cronometro.status === 'rodando', em: Date.now() };
        setSegundos(partida.cronometro.segundos);
      }
    } catch (error) {
      if (!silencioso) setToast({ severity: 'error', message: 'Erro ao carregar a partida.' });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Sincroniza com outros aparelhos a cada 5 s (quando não há diálogo aberto).
  useEffect(() => {
    const id = setInterval(() => {
      if (!jogadorSelecionado && !confirmarEncerrar && !goleirosAbertos && !enviando) carregar(true);
    }, 5000);
    return () => clearInterval(id);
  }, [carregar, jogadorSelecionado, confirmarEncerrar, goleirosAbertos, enviando]);

  // Relógio local a partir do tempo do servidor.
  useEffect(() => {
    const id = setInterval(() => {
      const b = base.current;
      const atual = b.rodando ? b.segundos + Math.floor((Date.now() - b.em) / 1000) : b.segundos;
      setSegundos(atual);
      if (atual >= TEMPO_PARTIDA && b.rodando && !avisou.current) {
        avisou.current = true;
        if (navigator.vibrate) navigator.vibrate([400, 200, 400]);
        setToast({ severity: 'warning', message: 'Tempo regulamentar encerrado (10 min).' });
      }
      if (atual < TEMPO_PARTIDA) avisou.current = false;
    }, 500);
    return () => clearInterval(id);
  }, []);

  const executar = async (fn, sucesso) => {
    setEnviando(true);
    try {
      const response = await fn();
      if (sucesso) setToast({ severity: 'success', message: sucesso });
      return response;
    } catch (error) {
      setToast({ severity: 'error', message: error.response?.data?.error || 'Não foi possível concluir a ação.' });
      return null;
    } finally {
      setEnviando(false);
    }
  };

  const partida = estado?.partida;

  const cronometro = async (acao) => {
    const response = await executar(() => api.post(`arbitragem/partidas/${partida._id}/cronometro`, { acao }));
    if (response) aplicarPartida(response.data.partida);
  };

  const lance = async (tipo) => {
    const { time, user_name } = jogadorSelecionado;
    setJogadorSelecionado(null);
    if (navigator.vibrate) navigator.vibrate(60);
    const label = labelLance(tipo);
    const response = await executar(() => api.post(`arbitragem/partidas/${partida._id}/eventos`, { tipo, time, user_name }), `${label} · ${nomeCurto(user_name)}`);
    if (response) aplicarPartida(response.data.partida);
  };

  const desfazer = async (evento) => {
    const response = await executar(() => api.remove(`arbitragem/partidas/${partida._id}/eventos/${evento._id}`), 'Lance desfeito');
    if (response) aplicarPartida(response.data.partida);
  };

  const salvarGoleiros = async () => {
    const response = await executar(() => api.put(`arbitragem/partidas/${partida._id}/goleiros`, { goleiros }), 'Goleiros definidos');
    if (response) aplicarPartida(response.data.partida);
    setGoleirosAbertos(false);
  };

  const encerrar = async () => {
    setConfirmarEncerrar(false);
    const response = await executar(() => api.post(`arbitragem/partidas/${partida._id}/encerrar`), 'Partida encerrada · próxima partida montada');
    if (response) {
      setEstado(response.data);
      const nova = response.data.partida;
      if (nova) {
        base.current = { segundos: nova.cronometro.segundos, rodando: false, em: Date.now() };
        setSegundos(nova.cronometro.segundos);
      }
    }
  };

  // Formação dos times pelo ranking; sem avaliação nem ranking, a API pede o pote e o pedido é reenviado
  const [pedirPotes, setPedirPotes] = useState(null);
  const sortear = async (potes) => {
    setEnviando(true);
    try {
      const response = await api.post('arbitragem/sortear', potes ? { potes } : {});
      setPedirPotes(null);
      setEstado(response.data);
      setToast({ severity: 'success', message: 'Times formados' });
    } catch (error) {
      const dados = error.response?.data || {};
      if (dados.code === 'POTE_NECESSARIO') setPedirPotes(dados.jogadores || []);
      else {
        setPedirPotes(null);
        setToast({ severity: 'error', message: dados.error || 'Não foi possível formar os times.' });
        if (error.response?.status === 409) carregar();
      }
    } finally {
      setEnviando(false);
    }
  };

  const contagem = (time, nome) => contarLances(partida?.eventos, time, nome);

  if (carregando && !estado) return <AppLayout><LoadingState label="Carregando partida..." /></AppLayout>;

  if (escalaEquipe && estado?.pelada) {
    return (
      <AppLayout maxWidth="sm">
        <Typography variant="h5" sx={{ mb: 0.5 }}>Check-in do árbitro</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>Faça o check-in para liberar a tela das partidas.</Typography>
        <CheckinEquipe escala={escalaEquipe} papel="arbitro" onCheckin={() => carregar()} />
      </AppLayout>
    );
  }

  if (!estado?.pelada) {
    return (
      <AppLayout maxWidth="md">
        <Card sx={{ mt: 2 }}><EmptyState icon={<SportsRoundedIcon />} title="Nenhuma pelada aberta" description="Quando a diretoria abrir a pelada e os jogadores fizerem check-in, as partidas aparecem aqui." action={<Button onClick={() => carregar()}>Atualizar</Button>} /></Card>
      </AppLayout>
    );
  }

  if (!partida) {
    return (
      <AppLayout maxWidth="md">
        <Card sx={{ mt: 2 }}>
          <EmptyState
            icon={<ShuffleRoundedIcon />}
            title="Nenhuma partida em andamento"
            description={`${estado.pelada.concentracao} jogadores na concentração. É preciso pelo menos 8 para sortear os times.`}
            action={<Button variant="contained" size="large" startIcon={<ShuffleRoundedIcon />} onClick={() => sortear()} disabled={enviando}>Sortear times</Button>}
          />
        </Card>
        <PotesDialog jogadores={pedirPotes} enviando={enviando} onConfirmar={(potes) => sortear(potes)} onFechar={() => setPedirPotes(null)} />
        <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
        </Snackbar>
      </AppLayout>
    );
  }

  const rodando = partida.cronometro.status === 'rodando';
  const [golsA, golsB] = partida.placar;
  const acabouTempo = segundos >= TEMPO_PARTIDA;

  return (
    <AppLayout maxWidth="md">
      {/* Placar e cronômetro */}
      <Card sx={{ position: 'sticky', top: { xs: 64, sm: 72 }, zIndex: 5, mb: 2, bgcolor: tokens.color.ink, color: '#fff', border: 'none', overflow: 'hidden' }}>
        <Grid container alignItems="stretch">
          {[0, 1].map((time) => (
            <Grid item xs={4} key={time} order={time === 0 ? 1 : 3} sx={{ bgcolor: cores[time], py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', fontWeight: 700 }}>Time {time + 1}</Typography>
              <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: { xs: '3.2rem', sm: '4rem' }, lineHeight: 1 }}>{time === 0 ? golsA : golsB}</Typography>
              {partida.goleiros[time] && <Typography variant="caption" sx={{ opacity: 0.8 }}>GK {nomeCurto(partida.goleiros[time])}</Typography>}
            </Grid>
          ))}
          <Grid item xs={4} order={2} sx={{ py: 1.5, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: { xs: '2.2rem', sm: '2.8rem' }, lineHeight: 1, color: acabouTempo ? tokens.color.brandLight : '#fff' }}>
              {formatar(segundos)}
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mt: 0.75 }}>
              <IconButton onClick={() => cronometro(rodando ? 'pausar' : 'iniciar')} disabled={enviando} sx={{ bgcolor: rodando ? 'rgba(255,255,255,0.15)' : tokens.color.success, color: '#fff', '&:hover': { bgcolor: rodando ? 'rgba(255,255,255,0.25)' : '#17864a' } }} aria-label={rodando ? 'Pausar' : 'Iniciar'}>
                {rodando ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
              <Tooltip title="Zerar cronômetro">
                <IconButton onClick={() => cronometro('zerar')} disabled={enviando} sx={{ color: 'rgba(255,255,255,0.7)' }} aria-label="Zerar"><RestartAltRoundedIcon /></IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
        Toque no jogador para lançar gol, assistência ou cartão
      </Typography>

      {/* Jogadores */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[0, 1].map((time) => (
          <Grid item xs={6} key={time}>
            <Stack spacing={1}>
              {(partida.times[time] || []).map((jogador) => {
                const c = contagem(time, jogador.user_name);
                const expulso = c.cartoes.includes('vermelho');
                return (
                  <Button
                    key={jogador.user_name}
                    onClick={() => setJogadorSelecionado({ time, user_name: jogador.user_name })}
                    disabled={enviando}
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      py: 1.5,
                      px: 1.5,
                      minHeight: 60,
                      borderRadius: 2,
                      color: tokens.color.text,
                      bgcolor: 'background.paper',
                      border: `2px solid ${cores[time]}`,
                      opacity: expulso ? 0.5 : 1,
                      '&:hover': { bgcolor: time === 0 ? tokens.color.brandSoft : tokens.color.surfaceAlt },
                    }}
                  >
                    <Typography fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: '0.9rem', sm: '1rem' }, textDecoration: expulso ? 'line-through' : 'none' }}>
                      {nomeCurto(jogador.user_name)}
                    </Typography>
                    <Box sx={{ ml: 0.5 }}><MarcadoresLance contagem={c} /></Box>
                  </Button>
                );
              })}
            </Stack>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Button variant="outlined" fullWidth startIcon={<SportsHandballRoundedIcon />} onClick={() => { setGoleiros((partida.goleiros || []).map((g) => ((estado.goleiros || []).includes(g) ? g : ''))); setGoleirosAbertos(true); }}>
          Goleiros
        </Button>
        <Button variant="contained" color="secondary" fullWidth startIcon={<FlagRoundedIcon />} onClick={() => setConfirmarEncerrar(true)} disabled={enviando} sx={{ py: 1.5 }}>
          Encerrar partida
        </Button>
      </Stack>

      {/* Lances */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lances da partida</Typography>
        {partida.eventos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhum lance registrado.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {partida.eventos.map((e) => (
              <Stack key={e._id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.5, px: 1, borderRadius: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="caption" sx={{ width: 44, color: 'text.secondary', fontFamily: 'monospace' }}>{formatar(e.segundos || 0)}</Typography>
                <IconeLance tipo={e.tipo} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cores[e.time] }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{nomeCurto(e.user_name)}</Typography>
                <Tooltip title="Desfazer">
                  <IconButton size="small" onClick={() => desfazer(e)} disabled={enviando}><UndoRoundedIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      {/* Fila e resultados */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Próximos (fila)</Typography>
            {estado.espera.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Fila vazia.</Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {estado.espera.map((j, i) => <Chip key={j.user_name} size="small" color={i < 4 ? 'success' : 'default'} variant={i < 4 ? 'filled' : 'outlined'} label={`${i + 1}. ${nomeCurto(j.user_name)}`} />)}
              </Stack>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Resultados de hoje</Typography>
            {estado.resultados.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhuma partida encerrada.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {estado.resultados.map((r) => (
                  <Stack key={r.numero} direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Partida {r.numero}</Typography>
                    <Typography variant="body2" fontWeight={700}>{r.placar[0]} × {r.placar[1]} · {r.vencedor === -1 ? 'Empate' : `Time ${r.vencedor + 1}`}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Ação sobre o jogador */}
      <LanceDialog jogador={jogadorSelecionado} onLance={lance} onClose={() => setJogadorSelecionado(null)} enviando={enviando} />

      {/* Goleiros */}
      <Dialog open={goleirosAbertos} onClose={() => setGoleirosAbertos(false)} fullWidth maxWidth="xs">
        <DialogTitle>Goleiros da partida</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {(estado.goleiros || []).length === 0 ? (
              <Alert severity="warning">Nenhum goleiro escalado para esta pelada. Peça à diretoria para escalar em Confirmação / Ordem de chegada.</Alert>
            ) : (
              [0, 1].map((time) => (
                <TextField
                  key={time}
                  select
                  label={`Goleiro do time ${time + 1}`}
                  value={(estado.goleiros || []).includes(goleiros[time]) ? goleiros[time] : ''}
                  onChange={(e) => setGoleiros((atual) => (time === 0 ? [e.target.value, atual[1]] : [atual[0], e.target.value]))}
                  fullWidth
                >
                  <MenuItem value=""><em>Sem goleiro</em></MenuItem>
                  {(estado.goleiros || []).map((nome) => <MenuItem key={nome} value={nome}>{nome}</MenuItem>)}
                </TextField>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGoleirosAbertos(false)} color="inherit">Cancelar</Button>
          <Button onClick={salvarGoleiros} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Encerrar */}
      <Dialog open={confirmarEncerrar} onClose={() => setConfirmarEncerrar(false)}>
        <DialogTitle>Encerrar partida?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Placar final <strong>{golsA} × {golsB}</strong> — {golsA === golsB ? 'empate' : `vitória do Time ${golsA > golsB ? 1 : 2}`}. A próxima partida será montada com a fila de espera.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmarEncerrar(false)} color="inherit">Voltar</Button>
          <Button onClick={encerrar} variant="contained" disabled={enviando}>Encerrar</Button>
        </DialogActions>
      </Dialog>

      {enviando && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1400 }}><CircularProgress size={28} /></Box>
      )}
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default ArbitroPage;
